import React, { useState, useRef, useEffect } from "react";

interface ScreenshotSelectorProps {
  onCapture: (screenshotBlob: Blob) => void;
  onCancel: () => void;
}

export const ScreenshotSelector: React.FC<ScreenshotSelectorProps> = ({
  onCapture,
  onCancel,
}) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [startPoint, setStartPoint] = useState<{ x: number; y: number } | null>(
    null
  );
  const [endPoint, setEndPoint] = useState<{ x: number; y: number } | null>(
    null
  );
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
      }
    };

    const handleMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      console.log("🖱️ Mouse down - starting selection");
      setIsSelecting(true);
      setStartPoint({ x: e.clientX, y: e.clientY });
      setEndPoint({ x: e.clientX, y: e.clientY });
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isSelecting) {
        e.preventDefault();
        e.stopPropagation();

        setEndPoint({ x: e.clientX, y: e.clientY });
      }
    };

    const handleMouseUp = async (e: MouseEvent) => {
      if (!isSelecting) return;

      e.preventDefault();
      e.stopPropagation();

      console.log("🖱️ Mouse up - ending selection");
      setIsSelecting(false);

      if (!startPoint || !endPoint) {
        onCancel();
        return;
      }

      // Calculate selection area
      const left = Math.min(startPoint.x, endPoint.x);
      const top = Math.min(startPoint.y, endPoint.y);
      const width = Math.abs(endPoint.x - startPoint.x);
      const height = Math.abs(endPoint.y - startPoint.y);

      console.log("📐 Selection area:", { left, top, width, height });

      if (width < 20 || height < 20) {
        console.log("❌ Selection too small, canceling");
        onCancel();
        return;
      }

      try {
        console.log("📸 Starting screenshot capture...");

        // Hide the overlay completely
        if (overlayRef.current) {
          overlayRef.current.style.display = "none";
        }

        // Wait a bit for overlay to disappear
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Create simple screenshot marker
        console.log("📷 Creating screenshot marker");
        await tryHtml2Canvas(left, top, width, height);
      } catch (error) {
        console.error("💥 Screenshot capture failed:", error);
        // Ensure overlay is restored on error
        if (overlayRef.current) {
          overlayRef.current.style.display = "block";
        }
        createFallbackScreenshot(width, height, left, top);
      }
    };

    const tryHtml2Canvas = async (
      left: number,
      top: number,
      width: number,
      height: number
    ) => {
      try {
        console.log("📷 Taking screenshot using browser API...");

        // Use the browser's built-in screenshot API
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: true,
        });

        const video = document.createElement("video");
        video.srcObject = stream;
        video.play();

        await new Promise((resolve) => {
          video.onloadedmetadata = resolve;
        });

        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d")!;

        // Calculate the scale factor
        const scaleX = video.videoWidth / window.screen.width;
        const scaleY = video.videoHeight / window.screen.height;

        ctx.drawImage(
          video,
          left * scaleX,
          top * scaleY,
          width * scaleX,
          height * scaleY,
          0,
          0,
          width,
          height
        );

        // Stop the stream
        stream.getTracks().forEach((track) => track.stop());

        console.log("✅ Screenshot captured successfully!");

        canvas.toBlob(
          (blob) => {
            if (blob) {
              onCapture(blob);
            } else {
              createFallbackScreenshot(width, height, left, top);
            }
          },
          "image/jpeg",
          0.9
        );
      } catch (error) {
        console.log("❌ Browser screenshot failed:", error);
        console.log("📷 Falling back to simple method...");

        // Simple fallback - just create a basic screenshot representation
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(width, 400);
        canvas.height = Math.max(height, 300);
        const ctx = canvas.getContext("2d")!;

        // Fill with white background
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Add a border
        ctx.strokeStyle = "#007bff";
        ctx.lineWidth = 4;
        ctx.strokeRect(2, 2, canvas.width - 4, canvas.height - 4);

        // Add screenshot text
        ctx.fillStyle = "#333333";
        ctx.font = "bold 18px Arial";
        ctx.textAlign = "center";
        ctx.fillText("📸 Screenshot", canvas.width / 2, canvas.height / 2 - 20);

        ctx.font = "14px Arial";
        ctx.fillStyle = "#666666";
        ctx.fillText(
          `${width} × ${height} pixels`,
          canvas.width / 2,
          canvas.height / 2 + 10
        );
        ctx.fillText(
          window.location.hostname,
          canvas.width / 2,
          canvas.height / 2 + 30
        );

        console.log("✅ Fallback screenshot created");

        canvas.toBlob(
          (blob) => {
            if (blob) {
              onCapture(blob);
            }
          },
          "image/jpeg",
          0.8
        );
      }
    };

    const createFallbackScreenshot = (
      width: number,
      height: number,
      left: number,
      top: number
    ) => {
      console.log("🎨 Creating visual selection marker...");

      const canvas = document.createElement("canvas");
      canvas.width = Math.max(400, width);
      canvas.height = Math.max(300, height);
      const ctx = canvas.getContext("2d");

      if (ctx) {
        // Create a nice visual representation
        const gradient = ctx.createLinearGradient(
          0,
          0,
          canvas.width,
          canvas.height
        );
        gradient.addColorStop(0, "#f8fafc");
        gradient.addColorStop(1, "#e2e8f0");

        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Add border
        ctx.strokeStyle = "#4f46e5";
        ctx.lineWidth = 3;
        ctx.strokeRect(3, 3, canvas.width - 6, canvas.height - 6);

        // Add screenshot icon
        ctx.fillStyle = "#4f46e5";
        ctx.font = "24px Arial";
        ctx.textAlign = "center";
        ctx.fillText("📸", canvas.width / 2, canvas.height / 2 - 40);

        // Add info text
        ctx.fillStyle = "#1e293b";
        ctx.font = "bold 16px Arial";
        ctx.fillText(
          "Selected Area Screenshot",
          canvas.width / 2,
          canvas.height / 2 - 5
        );

        ctx.fillStyle = "#64748b";
        ctx.font = "14px Arial";
        ctx.fillText(
          `${width}×${height}px at (${left}, ${top})`,
          canvas.width / 2,
          canvas.height / 2 + 20
        );
        ctx.fillText(
          window.location.hostname,
          canvas.width / 2,
          canvas.height / 2 + 40
        );
        ctx.fillText(
          new Date().toLocaleString(),
          canvas.width / 2,
          canvas.height / 2 + 60
        );

        canvas.toBlob(
          (blob) => {
            if (blob) {
              console.log("✅ Screenshot created successfully");
              onCapture(blob);
            } else {
              console.log("❌ Failed to create screenshot");
              onCancel();
            }
          },
          "image/jpeg",
          0.8
        );
      } else {
        onCancel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleMouseDown, true);
    document.addEventListener("mousemove", handleMouseMove, true);
    document.addEventListener("mouseup", handleMouseUp, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleMouseDown, true);
      document.removeEventListener("mousemove", handleMouseMove, true);
      document.removeEventListener("mouseup", handleMouseUp, true);
    };
  }, [onCancel, onCapture, isSelecting, startPoint, endPoint]);

  const selectionStyle =
    startPoint && endPoint
      ? {
          left: Math.min(startPoint.x, endPoint.x),
          top: Math.min(startPoint.y, endPoint.y),
          width: Math.abs(endPoint.x - startPoint.x),
          height: Math.abs(endPoint.y - startPoint.y),
        }
      : {};

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 cursor-crosshair"
      style={{
        zIndex: 2147483650,
        backgroundColor: "rgba(0, 0, 0, 0.2)",
        pointerEvents: "auto",
      }}
    >
      {/* Instructions */}
      <div
        className="fixed top-4 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-lg shadow-lg text-sm font-medium border"
        style={{ zIndex: 2147483651 }}
      >
        📸 Click and drag to select screenshot area • Press ESC to cancel
      </div>

      {/* Selection rectangle */}
      {startPoint && endPoint && (
        <div
          className="absolute border-2 border-blue-500 bg-blue-500 bg-opacity-20 pointer-events-none"
          style={{
            ...selectionStyle,
            zIndex: 2147483651,
            boxShadow: "0 0 0 1px rgba(255,255,255,0.5)",
          }}
        />
      )}
    </div>
  );
};
